import {useEffect, useState} from 'react';
import {createStyles, Box, Text, Group, rem, Title} from '@mantine/core';
import {IconListSearch} from '@tabler/icons-react';
import {useMediaQuery} from "@mantine/hooks";

const LINK_HEIGHT = 38;
const INDICATOR_SIZE = 13;
const INDICATOR_OFFSET = (LINK_HEIGHT - INDICATOR_SIZE) / 4;

const useStyles = createStyles((theme) => ({
    link: {
        ...theme.fn.focusStyles(),
        display: 'block',
        textDecoration: 'none',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
        lineHeight: rem(LINK_HEIGHT),
        fontSize: theme.fontSizes.sm,
        height: rem(LINK_HEIGHT),
        borderTopRightRadius: theme.radius.sm,
        borderBottomRightRadius: theme.radius.sm,
        borderLeft: `${rem(2)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,
        paddingRight: rem(15),

        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        },
    },

    linkActive: {
        fontWeight: 500,
        color: theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 3 : 7],
    },

    links: {
        position: 'relative',
        paddingLeft: rem(2),
        marginLeft: rem(10),
    },

    indicator: {
        transition: 'transform 150ms ease',
        border: `${rem(2)} solid ${
            theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 3 : 7]
        }`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        height: rem(INDICATOR_SIZE),
        width: rem(INDICATOR_SIZE),
        borderRadius: rem(INDICATOR_SIZE),
        position: 'absolute',
        left: `calc(-${rem(INDICATOR_SIZE)} / 2 + ${rem(1)})`,
    },
}));

interface TOCProps {
    links: { label: string; id: string; order: number }[];
    onChange: any
}

export function TOC({links, onChange}: TOCProps) {
    const {classes, cx} = useStyles();
    const [active, setActive] = useState(0);
    const isMobile = useMediaQuery('(max-width: 770px)');
    
    const updateActive = () => {
        // set the highest element in the viewport as active
        const elements = links.map((item) => document.getElementById(item.id));
        const new_active = elements.reduce((acc, element, index) => {
            if (element && element.getBoundingClientRect().top < 150) {
                return index;
            }
            return acc;
        }, 0);
        setActive(new_active);
    }
    
    useEffect(() => {
        updateActive()
        // throttle scroll event to prevent performance issues by using requestAnimationFrame
        let throttleTimeout: number | null = null;
        const updateActiveThrottled = () => {
            if (!throttleTimeout) {
                throttleTimeout = window.requestAnimationFrame(() => {
                    throttleTimeout = null;
                    updateActive();
                });
            }
        }
        window.addEventListener('scroll', updateActiveThrottled);

        return () => {
            window.removeEventListener('scroll', updateActive);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const items = links.map((item, index) => (
        <Box<'a'>
            component="a"
            href={item.id}
            onClick={(event) => {
                event.preventDefault();
                // @ts-ignore
                window.scrollTo({top: document.getElementById(item.id)?.offsetTop - 100,
                    behavior: 'smooth',
                });
                if (onChange !== null) {
                    onChange()
                }
            }}
            key={item.label}
            className={cx(classes.link, {[classes.linkActive]: active === index})}
            sx={(theme) => ({paddingLeft: `calc(${item.order} * ${theme.spacing.lg})`})}
        >
            <Text size="lg">
                {item.label}
            </Text>
        </Box>
    ));

    const NavElement = <>
        <Group mb="md" ml={"xl"}>
            <IconListSearch size="1.5rem" stroke={1.5}/>
            <Title order={3} style={{margin: '0 0 0 10px'}}>
                Inhalt
            </Title>
        </Group>
        <Box className={classes.links} ml={"xl"}>
            <div
                className={classes.indicator}
                style={{transform: `translateY(${rem(active * LINK_HEIGHT + INDICATOR_OFFSET)})`}}
            />
            {items}
        </Box>
    </>

    return (
        <div style={!isMobile ? {position: 'fixed', top: '100px', left:"10px"} : {left:"10px"}}>
            <div>
                {NavElement}
            </div>
        </div>
    )
}
